import {
  HubtypeFlowBuilderData,
  HubtypeFlowVersion,
  HubtypeUser,
} from '@hubtype/data-access-models'

import { FeedbackType } from '../../domain/feedback'
import { GENERIC_ERROR_MESSAGE_DESCRIPTION } from '../../UI/constants'
import { FlowBuilderApi } from './flow-builder-api'
import { HubtypeService } from './hubtype-service'

export class FlowBuilderService {
  // TODO: Stop using this endpoint after when removing flow builder from iframe
  static async getUserInfo(
    authToken: string
  ): Promise<{ botId: string; user: HubtypeUser } | undefined> {
    const data = await FlowBuilderApi.get(authToken, `flow_builder/user_info/`)
    if (data.error) return undefined
    const user = await HubtypeService.getUserData(authToken, data.user_id)
    if (!user) return undefined
    return { botId: data.bot_id, user }
  }

  static async loadFlow(
    authToken: string,
    flowVersion: string,
    botId: string
  ): Promise<HubtypeFlowBuilderData | undefined> {
    const data = await FlowBuilderApi.get(
      authToken,
      `bot_flows/${botId}/versions/${flowVersion}/`
    )
    return data.error ? undefined : data
  }

  static async saveFlow(
    authToken: string,
    flowData: HubtypeFlowBuilderData,
    botId: string
  ): Promise<FeedbackType> {
    const body = JSON.stringify(flowData)
    const data = await FlowBuilderApi.put(
      authToken,
      `bot_flows/${botId}/save/`,
      body
    )
    return data.error || FeedbackType.SUCCESS
  }

  static async publishFlow(
    authToken: string,
    version: string,
    botId: string
  ): Promise<FeedbackType> {
    const body = JSON.stringify({ version })
    const data = await FlowBuilderApi.put(
      authToken,
      `bot_flows/${botId}/publish/`,
      body
    )
    return data.error || FeedbackType.SUCCESS
  }

  static async getVersionHistory(
    authToken: string,
    botId: string
  ): Promise<HubtypeFlowVersion[] | { error: FeedbackType }> {
    const data = await FlowBuilderApi.get(
      authToken,
      `bot_flows/${botId}/versions/`
    )
    if (data.error) return data
    return data.map((v: any) => {
      const versionData = new HubtypeFlowVersion()
      versionData.version = v.version
      versionData.createdAt = new Date(v.created_at)
      versionData.createdBy = v.created_by
      versionData.isPublished = v.is_published
      return versionData
    })
  }

  static async uploadFile(
    authToken: string,
    file: File,
    botId: string
  ): Promise<string | null> {
    const sanitizedFileName = file.name.replace(/[\s?&=]/g, '-')
    const endpoint = `bot_flows/${botId}/medias/?filename=${sanitizedFileName}`
    const formData = new FormData()
    formData.append('file', file, sanitizedFileName)

    const data = await FlowBuilderApi.postFile(
      authToken,
      endpoint,
      formData,
      file.type
    )

    return data.error ? null : data.url
  }

  static async transferFlows(
    authToken: string,
    botId: string,
    targetBotId: string
  ): Promise<void> {
    try {
      await FlowBuilderApi.post(
        authToken,
        `bot_flows/${botId}/transfer/`,
        JSON.stringify({
          target_bot_id: targetBotId,
        })
      )
    } catch (error) {
      return Promise.reject({
        analytics: { code: 'transfer_failed' },
        feedback: {
          title: 'Failed to transfer flows.',
          description: GENERIC_ERROR_MESSAGE_DESCRIPTION,
        },
      })
    }
  }
}
